import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { List, Queue, CalendarBlank, Syringe, Users as UsersIcon, FileText, User, SignOut, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LEGACY_MAP = {
  "queue.manage": ["queue.call_next", "queue.complete", "queue.hold_resume", "queue.cancel"],
  "procedures.view": ["procedures.requests.view"],
};

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [perms, setPerms] = useState([]);
  const [clinicName, setClinicName] = useState("");

  useEffect(() => {
    api.get("/staff/me").then(({ data }) => {
      const rawPerms = data.staff_permissions || [];
      const expanded = [];
      for (const p of rawPerms) {
        if (LEGACY_MAP[p]) expanded.push(...LEGACY_MAP[p]);
        else expanded.push(p);
      }
      setPerms([...new Set(expanded)]);
      const name = data.doctors?.map((d) => d.clinic_name || d.name).filter(Boolean).join(", ") || "Clinic";
      setClinicName(name);
    }).catch(() => {});
  }, []);

  const can = (perm) => perms.includes(perm);

  const sidebarItems = [
    { to: "/staff", label: "Today's Queue", icon: Queue, perm: "queue.view", end: true },
    { to: "/staff/appointments", label: "All Appointments", icon: CalendarBlank, perm: "appointments.view" },
    { to: "/staff/future-appointments", label: "Future Appointments", icon: CalendarBlank, perm: "appointments.view" },
    { to: "/staff/treatment-plans", label: "Treatment Plans", icon: FileText, perm: "treatment_plans.view" },
    { to: "/staff/patients", label: "Patients", icon: UsersIcon, perm: "patients.view" },
    { to: "/staff/import-patients", label: "Import Patients", icon: UploadSimple, perm: "patients.import" },
    { to: "/staff/procedures", label: "Procedures", icon: Syringe, perm: "procedures.requests.view" },
    { to: "/staff/reschedule-center", label: "Reschedule", icon: WarningCircle, perm: "appointments.view" },
  ].filter((item) => can(item.perm));

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 bg-card border-r border-border transform transition-all duration-200 lg:translate-x-0 lg:static lg:inset-auto w-64 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-2 h-16 px-5 border-b border-border">
          <Link to="/staff" className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-sm shrink-0">
            {clinicName ? clinicName.charAt(0).toUpperCase() : "C"}
          </Link>
          <Link to="/staff" className="font-display font-bold text-lg text-foreground min-w-0 max-w-[160px] leading-tight break-words" title={clinicName}>
            {clinicName}
          </Link>
        </div>
        <nav className="p-2 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <Icon size={20} weight="duotone" className="shrink-0" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-border p-3">
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
            <User size={16} /> <span className="truncate">{user?.name}</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors mt-1">
            <SignOut size={16} /> Log out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top navbar */}
        <header className="h-16 border-b border-border bg-card flex items-center gap-4 px-4 lg:px-6 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-muted text-muted-foreground">
            <List size={22} />
          </button>

          <div className="flex-1" />

          {/* Profile */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
            <User size={16} />
            <span className="hidden sm:inline truncate max-w-[120px]">{user?.name}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
